import AddBusinessRounded from "@mui/icons-material/AddBusinessRounded";
import { Box, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { DuplicateRecordCell } from "../../../components/team/DuplicateEmployees/DuplicateRecordCell";
import { type MatchedField, STATIC_PAIR_COLUMN } from "../../../components/team/DuplicateEmployees/duplicatePairs";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import type { SystemGridColDef } from "../../../components/UIToolkit/Tables/SystemDataGrid/types";
import { DUPLICATE_PAIR, ROSTER_MERGE_GRID } from "../../../theme/tokens";
import { type HeldOutcome, type HeldReviewRow, type HeldSource, rowContact } from "./heldRowModel";

type Options = {
	dataTestId: string;
	facility: string;
	outcomes: ReadonlyMap<number, HeldOutcome>;
	busy: boolean;
	onAdd: (row: HeldReviewRow) => void;
	onCreate: (source: HeldSource) => void;
};

// The held row and the record share exactly one thing: the name.
const NAME_MATCH: ReadonlySet<MatchedField> = new Set(["name"]);

const OUTCOME_LABEL: Record<Exclude<HeldOutcome["kind"], "error">, string> = {
	added: "onboarding_load.held.outcome.added",
	already: "onboarding_load.held.outcome.already",
	created: "onboarding_load.held.outcome.created",
};

/** The row as uploaded: its name, and its email and phone so they can be compared with the record's. */
const uploadedCell = (row: HeldReviewRow, t: TFunction, dataTestId: string) => {
	const { email, phone } = rowContact(row.source);
	const contact = [email, phone].filter((value): value is string => value !== undefined);
	return (
		<Box
			sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%", lineHeight: "normal", minWidth: 0 }}
			data-testid={`${dataTestId}-held-uploaded-${row.key}`}
		>
			<Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
				{row.uploadedName}
			</Typography>
			{(contact.length > 0 ? contact : [t("onboarding_load.held.no_contact")]).map((line) => (
				<Typography key={line} variant="caption" color="text.secondary" noWrap>
					{line}
				</Typography>
			))}
		</Box>
	);
};

const actionCell = (row: HeldReviewRow, t: TFunction, { dataTestId, facility, outcomes, busy, onAdd, onCreate }: Options) => {
	const outcome = outcomes.get(row.source.index);
	// A resolved row shows what happened on each of its candidate lines, so none still offers an action.
	if (outcome && outcome.kind !== "error") {
		return (
			<MetaChip
				tone={outcome.kind === "already" ? "neutral" : "success"}
				label={t(OUTCOME_LABEL[outcome.kind])}
				dataTestId={`${dataTestId}-held-outcome-${row.key}`}
			/>
		);
	}
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "stretch",
				gap: 0.5,
				width: "100%",
				height: "100%",
				lineHeight: "normal",
			}}
		>
			{/* A failed action keeps the buttons: the onboarder can retry or choose the other way. */}
			{outcome && (
				<Typography variant="caption" color="error" sx={{ whiteSpace: "normal" }} data-testid={`${dataTestId}-held-outcome-${row.key}`}>
					{outcome.reason}
				</Typography>
			)}
			<SystemButton
				buttonVariant="primary"
				size="compact"
				leadingIcon={<AddBusinessRounded />}
				onClick={() => onAdd(row)}
				disabled={busy}
				aria-label={t("onboarding_load.held.add_aria", { facility, name: row.uploadedName, id: row.candidate.nurseId })}
				// The consequence, where the choice is made; the visible label stays short enough for the column.
				tooltip={t("onboarding_load.held.add_tooltip", { facility })}
				noFullWidthMobile
				sx={{ width: "100%" }}
				data-testid={`${dataTestId}-held-add-${row.key}`}
			>
				{t("onboarding_load.held.add")}
			</SystemButton>
			{row.firstOfSource && (
				<SystemButton
					buttonVariant="borderless"
					size="compact"
					onClick={() => onCreate(row.source)}
					disabled={busy}
					aria-label={t("onboarding_load.held.create_aria", { name: row.uploadedName })}
					tooltip={t("onboarding_load.held.create_tooltip")}
					noFullWidthMobile
					sx={{ width: "100%", justifyContent: "center" }}
					data-testid={`${dataTestId}-held-create-${row.source.index}`}
				>
					{t("onboarding_load.held.create")}
				</SystemButton>
			)}
		</Box>
	);
};

export const buildHeldRowColumns = (t: TFunction, options: Options): SystemGridColDef<HeldReviewRow>[] => [
	{
		...STATIC_PAIR_COLUMN,
		field: "uploadedName",
		headerName: t("onboarding_load.held.column.uploaded"),
		flex: 0.7,
		minWidth: ROSTER_MERGE_GRID.RECORD_MIN_WIDTH,
		renderCell: ({ row }) => uploadedCell(row, t, options.dataTestId),
	},
	{
		...STATIC_PAIR_COLUMN,
		field: "candidate",
		headerName: t("onboarding_load.held.column.existing"),
		flex: 1,
		minWidth: ROSTER_MERGE_GRID.RECORD_MIN_WIDTH,
		renderCell: ({ row }) => <DuplicateRecordCell record={row.candidate} matched={NAME_MATCH} dataTestId={`${options.dataTestId}-held-record-${row.key}`} />,
	},
	{
		...STATIC_PAIR_COLUMN,
		field: "actions",
		headerName: t("onboarding_load.held.column.action"),
		width: DUPLICATE_PAIR.ACTIONS_COLUMN_WIDTH,
		renderCell: ({ row }) => actionCell(row, t, options),
	},
];
